import { makeStyles } from '@mui/styles';
import { Divider } from 'apollo-react';
import Grid from 'apollo-react/components/Grid';
import Paper from 'apollo-react/components/Paper';
import Typography from 'apollo-react/components/Typography';
import qs from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import HierarchyTree from './components/HierarchyTree';
import { displayEntity } from '../../actions/EntityDisplayActions';
import { clearHierarchy, fetchProductHierarchy } from '../../actions/ProductHierarchyActions';
import CustomLoader from '../../components/CustomLoader';
import Layout from '../../components/Layout';
import { useTranslation } from '../../custom-hooks/useTranslation';
import DetailForm from '../../form-designer/components/DetailForm';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { isFormLoading } from '../../form-designer/selectors/common.selectors';
import { getHierarchy, getHierarchyLoading } from '../../selectors/ProductHierarchySelectors';
import { getUITA } from '../../utils/commonUtils';
import { getColorByNodeName, getHierarchyLevels } from '../../utils/productHierarchyUtils';
import { isEmpty, isNilOrEmpty } from '../../utils/ramdaUtils';

const useStyles = makeStyles({
  detailFormContainer: {
    padding: 30,
  },
  detailForm: {
    backgroundColor: '#fff',
    padding: '20px 35px',
    borderRadius: '5px',
  },
  hierarchyContainer: {
    maxWidth: '32%',
    width: '100%',
    position: 'fixed',
    backgroundColor: '#fff',
    borderRight: 'solid 1px #dfdfdf',
    padding: 24,
    paddingRight: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',

    '@media (max-width: 900px)': {
      position: 'unset',
      maxWidth: '100%',
      justifyContent: 'unset',
    },
  },
  treeContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,

    '@media (max-width: 900px)': {
      maxHeight: 'unset',
    },
  },
  divider: {
    marginLeft: -24,
  },
  legendContainer: {
    marginTop: 'auto',
  },
  legendList: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: '10px 0',
    marginBottom: 70,

    '& li span': {
      padding: '0 10px',
      marginRight: 0,
      borderRadius: '2px',
    },

    '@media (max-width: 900px)': {
      marginBottom: 0,
    },
  },
  loaderContainer: {
    marginTop: '100px',
  },
});

const ProductHierarchy = () => {
  const [defaultSelected, setDefaultSelected] = useState('');
  const [defaultFormKey, setDefaultFormKey] = useState('');

  const isDetailFormLoading = useSelector(isFormLoading(FORM_KEYS.DETAIL));
  const hierarchy = useSelector(getHierarchy);
  const isLoading = useSelector(getHierarchyLoading);

  const location = useLocation();
  const history = useHistory();

  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const qsParams = qs.parse(location.search) || {};

  useEffect(() => {
    document.title = translateString('Product Hierarchy');

    //set the selected product id for the initial tree render
    if (qsParams.itmId && isEmpty(defaultSelected)) {
      setDefaultSelected(qsParams.itmId);
    }

    dispatch(fetchProductHierarchy({ itmId: qsParams.itmId, entityName: qsParams.entityName }));

    return () => {
      dispatch(clearHierarchy());
      sessionStorage.removeItem('backButtonURL');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isNilOrEmpty(defaultFormKey)) {
      dispatch(
        displayEntity(
          qsParams.itmId,
          qsParams.entityName,
          qsParams.formKey ?? defaultFormKey,
          '',
          '',
          '',
          '',
          '',
          true
        )
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFormKey, location]);

  const onNodeClick = useCallback(({ entityName, itemId, formKey }) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('entityName', entityName);
    searchParams.set('itmId', itemId);
    searchParams.set('formKey', formKey);

    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLegend = () => {
    const legendLevels = getHierarchyLevels(hierarchy);

    return (
      <ul ui-ta={getUITA('HierarchyLegend')} className={classes.legendList}>
        {legendLevels.map((level) => (
          <li key={level.name}>
            <span style={{ background: getColorByNodeName(level.name) }} />
            <Typography variant="caption">{translateString(level.label)}</Typography>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Layout>
      <Grid container>
        <Grid item xs={12} md={4}>
          {isLoading ? (
            <div className={classes.loaderContainer}>
              <CustomLoader isInner={true} />
            </div>
          ) : (
            <div className={classes.hierarchyContainer}>
              {!isEmpty(hierarchy) && (
                <>
                  <div className={classes.treeContainer}>
                    <Typography variant="h2" gutterBottom>
                      {translateString('Product Hierarchy')}
                    </Typography>
                    <HierarchyTree
                      hierarchy={hierarchy}
                      defaultSelected={defaultSelected}
                      setDefaultFormKey={setDefaultFormKey}
                      onNodeClick={onNodeClick}
                    />
                  </div>
                  <div className={classes.legendContainer}>
                    <Divider className={classes.divider} />
                    {renderLegend()}
                  </div>
                </>
              )}
            </div>
          )}
        </Grid>
        <Grid className={classes.detailFormContainer} item xs={12} md={8}>
          <Paper className={classes.detailForm}>
            <DetailForm isLoading={isDetailFormLoading} />
          </Paper>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default ProductHierarchy;
